import { createFileRoute, Link } from "@tanstack/react-router";
import { socialMeta } from "@/lib/social-meta";
import { ORG_WEBSITE_JSONLD } from "@/lib/structured-data";
import { MarketingShell, MarketingHero, CtaBand } from "@/components/MarketingShell";
import { PageContainer } from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { Check } from "lucide-react";
import { useState } from "react";

export const Route = createFileRoute("/pricing")({
  component: PricingPage,
  head: () => ({
    meta: [
      { title: "Pricing — MissionReady Patrol & Field Ops Platform" },
      { name: "description", content: "Simple per-org pricing for security and patrol teams. Free plan, Solo $9, Starter $49, Pro $99, Enterprise $349/mo with white labeling and SSO." },
      ...socialMeta({
        title: "Pricing — MissionReady",
        description: "Simple per-org pricing for security and patrol teams. Free plan, Solo $9, Starter $49, Pro $99, Enterprise $349/mo with white labeling and SSO.",
        url: "https://missionready.dev/pricing",
      }),
    ],
    links: [{ rel: "canonical", href: "https://missionready.dev/pricing" }],
    scripts: [
      ...ORG_WEBSITE_JSONLD,{
      type: "application/ld+json",
      children: JSON.stringify({
        "@context": "https://schema.org",
        "@type": "Product",
        name: "MissionReady",
        description: "Multi-tenant patrol accountability and field operations platform for security teams.",
        brand: { "@type": "Brand", name: "MissionReady" },
        offers: [
          { "@type": "Offer", name: "Free", price: "0", priceCurrency: "USD" },
          { "@type": "Offer", name: "Solo Contractor", price: "9", priceCurrency: "USD" },
          { "@type": "Offer", name: "Starter Org", price: "49", priceCurrency: "USD" },
          { "@type": "Offer", name: "Pro Org", price: "99", priceCurrency: "USD" },
          { "@type": "Offer", name: "Enterprise", price: "349", priceCurrency: "USD" },
        ],
      }),
    }],
  }),
});

// Every module is included on every plan. The only paywalled items are
// white labeling and API access (Enterprise). Plans differ by flat monthly
// price and by seat / site limits — not by feature gating.
const EVERY_PLAN_INCLUDES = [
  "Voice D.A.R. reporting",
  "Incident reports with photo & video",
  "QR checkpoints, patrols & tours",
  "Live GPS, geofences & breadcrumb replay",
  "Scheduling, shift offers & time clock",
  "Dispatch, SOS & team messaging",
  "Client portal, invoicing & exports",
  "Custom report builder & analytics",
  "Offline field queue (scans, incidents, voice memos)",
];

const PLANS = [
  {
    key: "free", annual: null,
    name: "Free", price: "$0", unit: "/mo",
    tagline: "Flat rate. No credit card, no time limit.",
    features: ["2 sites", "3 seats", "Every module included", "Community support"],
    cta: "Start free",
    featured: false,
  },
  {
    key: "solo", annual: '$90',
    name: "Solo Contractor", price: "$9", unit: "/mo",
    tagline: "Flat rate for one contractor. 30-day free trial.",
    features: ["Up to 5 sites", "2 seats (you + 1 backup)", "Every module included", "Email support"],
    cta: "Start 30-day trial",
    featured: false,
  },
  {
    key: "starter", annual: '$470',
    name: "Starter Org", price: "$49", unit: "/mo",
    tagline: "Flat rate for the whole org. 30-day free trial.",
    features: ["Unlimited sites", "Up to 15 seats", "Every module included", "Email support"],
    cta: "Start 30-day trial",
    featured: false,
  },
  {
    key: "pro", annual: '$950',
    name: "Pro Org", price: "$99", unit: "/mo",
    tagline: "Flat rate for the whole org. 30-day free trial.",
    features: ["Unlimited sites", "Up to 50 seats", "Every module included", "Priority support"],
    cta: "Start 30-day trial",
    featured: true,
  },
  {
    key: "enterprise", annual: '$3,340',
    name: "Enterprise", price: "$349", unit: "/mo",
    tagline: "Flat rate, unlimited team. 30-day free trial.",
    features: ["Unlimited sites", "Unlimited seats", "Every module included", "White labeling", "API access", "Dedicated support"],
    cta: "Start 30-day trial",
    featured: false,
  },
];

function PricingPage() {
  const [interval, setInterval] = useState<"month" | "year">("month");
  return (
    <MarketingShell>
      <MarketingHero
        eyebrow="Pricing"
        title={<>Simple plans for <span className="text-primary">every team size.</span></>}
        subtitle="One flat monthly price per organization — never per report, per scan, or per site. Every module is included on every plan; plans differ only by how many seats and sites you need. Paid plans start with a 30-day free trial, no credit card required."
      />
      <section className="py-16 md:py-20">
        <PageContainer>
          <div className="mb-8 flex flex-wrap items-center justify-between gap-4">
            <h2 className="font-display text-3xl font-bold md:text-4xl">Plans &amp; pricing</h2>
            <div className="inline-flex rounded-lg border border-border bg-card p-1 text-sm" role="group" aria-label="Billing interval">
              <button type="button" aria-pressed={interval === "month"} onClick={() => setInterval("month")}
                className={`rounded-md px-4 py-1.5 font-medium transition-colors ${interval === "month" ? "bg-primary text-primary-foreground" : "text-muted-foreground"}`}>Monthly</button>
              <button type="button" aria-pressed={interval === "year"} onClick={() => setInterval("year")}
                className={`rounded-md px-4 py-1.5 font-medium transition-colors ${interval === "year" ? "bg-primary text-primary-foreground" : "text-muted-foreground"}`}>Yearly <span className="ml-1 text-xs opacity-80">2 months free</span></button>
            </div>
          </div>
          <div className="grid gap-5 lg:grid-cols-5 md:grid-cols-2">
            {PLANS.map((p) => (
              <div
                key={p.name}
                className={`relative flex flex-col rounded-2xl border p-6 transition-colors ${
                  p.featured
                    ? "border-primary bg-primary/5 shadow-[0_0_40px_-15px_var(--primary)]"
                    : "border-border bg-card hover:border-primary/50"
                }`}
              >
                {p.featured && (
                  <span className="absolute -top-3 left-1/2 -translate-x-1/2 rounded-full bg-primary px-3 py-1 text-[10px] font-bold uppercase tracking-wider text-primary-foreground">
                    Most popular
                  </span>
                )}
                <h3 className="font-display text-lg font-semibold">{p.name}</h3>
                <p className="mt-1 text-sm text-muted-foreground min-h-[2.5rem]">{p.tagline}</p>
                <div className="mt-4 flex items-baseline gap-1">
                  <span className="font-display text-4xl font-bold">{interval === "year" && p.annual ? p.annual : p.price}</span>
                  <span className="text-sm text-muted-foreground">{interval === "year" && p.annual ? "/yr" : p.unit}</span>
                </div>
                {interval === "year" && p.annual && (
                  <p className="mt-1 text-xs text-muted-foreground">Billed yearly · {p.price}/mo if paid monthly</p>
                )}
                <ul className="mt-6 flex-1 space-y-2.5">
                  {p.features.map((f) => (
                    <li key={f} className="flex items-start gap-2 text-sm">
                      <Check className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>
                <Button asChild className="mt-6 w-full" variant={p.featured ? "default" : "outline"}>
                  <Link to="/signup" search={{ plan: p.key, interval: p.annual ? interval : undefined }}>{p.cta}</Link>
                </Button>
              </div>
            ))}
          </div>

          <div className="mt-10 rounded-2xl border border-border bg-card/50 p-6 md:p-8">
            <h3 className="font-display text-xl font-semibold">Included on every plan — even Free</h3>
            <p className="mt-1 text-sm text-muted-foreground">
              White labeling and API access are the only Enterprise-only items.
            </p>
            <ul className="mt-5 grid gap-2.5 sm:grid-cols-2 lg:grid-cols-3">
              {EVERY_PLAN_INCLUDES.map((f) => (
                <li key={f} className="flex items-start gap-2 text-sm">
                  <Check className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
                  <span>{f}</span>
                </li>
              ))}
            </ul>
          </div>
        </PageContainer>
      </section>
      <CtaBand title="Not sure which plan fits?" subtitle="Start on Free or trial Pro for 30 days — you can switch any time." />
    </MarketingShell>
  );
}
